import { useEffect } from "react";
import type { Subscription } from "centrifuge";

import { runtimeConfig } from "@/config/env";
import { getRealtimeClient } from "@/lib/realtime-client";

export function useRealtimeChannel<T = unknown>(channel: string, onMessage: (data: T) => void) {
  useEffect(() => {
    if (!runtimeConfig.centrifugoUrl) {
      return undefined;
    }

    type SubscriptionInstance = Subscription;
    let subscription: SubscriptionInstance | null = null;
    let createdSubscription = false;
    let client: ReturnType<typeof getRealtimeClient> | null = null;

    try {
      client = getRealtimeClient();
      const existing = client.getSubscription(channel);
      if (existing) {
        subscription = existing as SubscriptionInstance;
      } else {
        subscription = client.newSubscription(channel) as SubscriptionInstance;
        createdSubscription = true;
      }
    } catch (error) {
      console.warn("[realtime] unable to initialize channel subscription", channel, error);
      return undefined;
    }

    const handler = (ctx: { data: T }) => {
      onMessage(ctx.data);
    };

    subscription.on("publication", handler);
    try {
      subscription.subscribe();
    } catch (error) {
      console.warn("[realtime] subscribe failed", channel, error);
    }

    return () => {
      if (!subscription || !client) {
        return;
      }

      subscription.off("publication", handler);

      try {
        if (createdSubscription) {
          client.removeSubscription(subscription);
        } else {
          subscription.unsubscribe();
        }
      } catch (error) {
        console.warn("[realtime] cleanup failed", channel, error);
      }
    };
  }, [channel, onMessage]);
}
